import React from 'react';
import { connect } from 'react-redux';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import { createSelector } from 'reselect';
import { DASHBOARD_TYPE } from '@utils/constants';
import * as tempDashboardActions from '@store/tempDashboard/actions';
import {
  savedKeysSelector,
  dashboardsSelector,
  afterInitializationSelector,
} from '@store/tempDashboard/selectors';
import { cloneWithoutSelectedMetrics } from '@utils/scrollingWindowFromTo';

const selector = createSelector(
  savedKeysSelector,
  afterInitializationSelector,
  dashboardsSelector,
  (keys, afterInitialization, dashboards) => ({ keys, afterInitialization, dashboards }),
);

const areEqual = (prevProps, nextProps) =>
  prevProps.dashboardType !== 2 &&
  nextProps.dashboardType !== 2 &&
  prevProps.id === nextProps.id &&
  prevProps.dashboardType === nextProps.dashboardType &&
  prevProps.isFetching === nextProps.isFetching &&
  isEqual(prevProps.response, nextProps.response) &&
  isEqual(prevProps.dashboard, nextProps.dashboard) &&
  isEqual(prevProps.keys, nextProps.keys);

// eslint-disable-next-line react/display-name
const UpdateProvider = React.memo(
  ({
    id,
    dashboardType,
    dashboard,
    response = null,
    isFetching,
    keys,
    children,
    initTempDashboard,
    createSavedDashboard,
    updateDashboard,
    dashboards = {},
    resetSavedDashboard,
  }) => {
    const savedKey = `saved-${id}`;
    const includesId = keys.includes(savedKey);
    const prevUnsavedDashboard = dashboards[savedKey];

    // const asyncUpdateDashboard = React.useCallback(debounce(updateDashboard, 300));

    React.useEffect(() => {
      if (!includesId && response) {
        // sd-note: this is invoked while saving a new dashboard
        createSavedDashboard(id, response.name, dashboardType, response.config);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, includesId, response]);

    React.useEffect(() => {
      if (includesId) {
        // sd-note: probably why the tab does not show up highlighted, when clicked from the dropdown
        initTempDashboard(savedKey);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, includesId]);

    React.useEffect(() => {
      const responseConfig = get(response, 'config', null);
      if (isFetching || !includesId || responseConfig === null) {
        return;
      }
      const clonedResponseConfig = cloneWithoutSelectedMetrics(responseConfig, dashboardType);
      const clonedDashboard = cloneWithoutSelectedMetrics(dashboard, dashboardType);
      if (
        dashboardType === DASHBOARD_TYPE.OVB ||
        dashboardType === DASHBOARD_TYPE.ML ||
        dashboardType === DASHBOARD_TYPE.VIEWER_MODULE
      ) {
        const responseBase = responseConfig && responseConfig.base;
        const dashboardBase = dashboard && dashboard.base;
        if (!isEqual(responseBase, dashboardBase)) {
          updateDashboard(id, dashboard, true);
        }
      } else if (!isEqual(clonedResponseConfig, clonedDashboard)) {
        updateDashboard(id, dashboard, true);
      } else if (isEqual(responseConfig, get(prevUnsavedDashboard, 'dashboard'))) {
        resetSavedDashboard(id, responseConfig);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, includesId, dashboard, response, isFetching]);

    return children();
  },
  areEqual,
);

export default connect(selector, {
  createSavedDashboard: tempDashboardActions.createSavedDashboard,
  updateDashboard: tempDashboardActions.setSaved,
  resetSavedDashboard: tempDashboardActions.resetSavedDashboard,
})(UpdateProvider);
